import { QuestionCircleOutlined } from '@ant-design/icons'
import clsx from 'clsx'
import { FormikContext } from 'formik'
import { forwardRef, useEffect, useImperativeHandle } from 'react'
import { useTranslation } from 'react-i18next'

import { useFormik } from '../../../../../common/hooks/formik'
import { PureInput } from '../../../../../common/ui/pure-input/pure-input'
import { Project } from '../../../../../entities/projects/types/project.types'
import { Role } from '../../../../roles/roles/types/roles-page-types'
import { UserInviteData } from '../../types/users.types'
import { AddUserModalValidationSchema } from '../../validators/add-user-modal-validation-schema'
import { SelectUserRoles } from '../select-user-roles/select-user-roles'

type AddUserModalProps = {
  onSubmit: (userData: UserInviteData) => void
  roles: Role[]
  projects: Project[]
  initialValues: UserInviteData | null
}

export const UserModal = forwardRef(({ onSubmit, initialValues, roles, projects }: AddUserModalProps, ref) => {
  const formik = useFormik({
    initialValues: initialValues ?? {
      email: '',
      roles: [] as number[],
      projects: [] as number[],
    },
    enableReinitialize: true,
    validationSchema: AddUserModalValidationSchema(),
    onSubmit: (values: UserInviteData) => {
      onSubmit(values)
      formik.resetForm()
    },
  })

  useEffect(() => {
    if (initialValues) {
      formik.resetForm({ values: initialValues })
    } else {
      formik.resetForm({
        values: {
          email: '',
          roles: [],
          projects: [],
        },
      })
    }
  }, [initialValues])

  useImperativeHandle(ref, () => ({
    submit: formik.submitForm,
    clear: formik.resetForm,
  }))

  const { t } = useTranslation()

  return (
    <FormikContext.Provider value={formik as any}>
      <form
        className="row"
        onSubmit={formik.handleSubmit}
      >
        <div className="col-12 col-md-3 px-1">
          <p className="mb-1">{t('usersPage.addNewUser.emailLabel')}</p>
          <PureInput
            {...formik.getFieldProps('email')}
            placeholder="example@mail.com"
          />
          <p className={clsx('text-secondary', formik.errors.email ? 'text-danger' : '')}>
            {formik.errors.email ? null : <QuestionCircleOutlined className="me-1" />}
            {t(`${formik.errors.email ? formik.errors.email : 'usersPage.addNewUser.emailDescription'}`)}
          </p>
        </div>

        <div className="col-12 col-md-5 px-1">
          <p className="mb-1">{t('usersPage.addNewUser.rolesLabel')}</p>
          <SelectUserRoles
            className="ms-0 mb-2 mb-md-0 w-100"
            data={roles || []}
            onChange={(roles) => formik.setFieldValue('roles', roles)}
          />
          <p className="text-secondary">
            <QuestionCircleOutlined className="me-1" />
            {t('usersPage.addNewUser.rolesDescription')}
          </p>
        </div>

        <div className="col-12 col-md-4 px-1">
          <p className="mb-1">{t('usersPage.addNewUser.projectsLabel')}</p>
          <SelectUserRoles
            className="ms-0 mb-2 mb-md-0 w-100"
            data={projects || []}
            onChange={(projects) => formik.setFieldValue('projects', projects)}
          />
          <p className="text-secondary">
            <QuestionCircleOutlined className="me-1" />
            {t('usersPage.addNewUser.projectsDescription')}
          </p>
        </div>
      </form>
    </FormikContext.Provider>
  )
})
